import { weaponCountMeter } from "../hud/weapon-count"
import {
  initializeVguiRuntime,
  type VguiOperation,
  type VguiPanelId,
  type VguiPanelSnapshot,
  type VguiResourceDocument,
  type VguiResourceNode,
  type VguiResourceSelection,
  type VguiRuntime,
  type VguiRuntimeLimits,
  type VguiRuntimeSnapshot,
  type VguiSectionedListItem,
  type VguiViewport,
} from "@playsrc/vgui"
import {
  adaptSessionHud,
  bindTf2Hud,
  bindTf2HudAction,
  TF2_HUD_DYNAMIC_IMAGES,
  TF2_SCOREBOARD_CLASS_IMAGES,
  TF2_SCOREBOARD_IMAGES,
  TF2_GROUPED_CONDITION_PANELS,
  TF2_INDEPENDENT_CONDITION_PANELS,
  tf2HudAvailable,
  tf2HudUnavailable,
  type SessionHudContext,
  type SessionSimulationPublication,
  type Tf2HudAction,
  type Tf2HudAvailability,
  type Tf2HudBinding,
  type Tf2HudCommand,
  type Tf2HudCrosshair,
  type Tf2HudPanelValue,
  type Tf2HudScoreboard,
  type Tf2HudSnapshot,
  type Tf2ScoreboardPlayer,
} from "../hud"
import type { CaptureObjectives, RoundSnapshot, Tf2Team } from "../codec"
import { TimerDeltas } from "./timer-delta"
import { tf2ClassPresentation } from "../class"
import type { Tf2VguiResources } from "../ui-integration"
import { Tf2HudCrosshairPresentation } from "./crosshair"
import { Tf2HudScopePresentation } from "./scope"
import { Tf2HudDeathNoticePresentation } from "./deathnotice"
import { ControlPointHud } from "./control-points"
import { Tf2HudDamagePresentation, type Tf2DamageIndicatorInput } from "./damage"

export type Tf2HudIntegrationDiagnostic = Readonly<{
  code: "VguiRejected" | "PanelUnavailable" | "ValueUnavailable" | "UnsupportedPanelValue" | "AnimationUnavailable"
  subject: string
}>

export type Tf2HudIntegrationSnapshot = Readonly<{
  binding: Tf2HudBinding | null
  vgui: VguiRuntimeSnapshot
  diagnostics: readonly Tf2HudIntegrationDiagnostic[]
  animationTrace: readonly string[]
}>

export type Tf2HudIntegrationProbe = Readonly<{
  panels: readonly VguiPanelSnapshot[]
  animationTrace: readonly string[]
}>

export type Tf2HudModelPanel = Readonly<{
  model: string
  skin: number
  fov: number
  origin: readonly [number, number, number]
  angles: readonly [number, number, number]
  bounds: Readonly<{ x: number; y: number; width: number; height: number }>
}>

export type Tf2HudIntegration = Readonly<{
  publish(publication: SessionSimulationPublication, context: SessionHudContext): Tf2HudBinding
  action(action: Tf2HudAction): Tf2HudAvailability<Tf2HudCommand>
  frame(timeSeconds: number, visible?: boolean): void
  setViewport(viewport: VguiViewport): void
  probe(): Tf2HudIntegrationProbe
  modelPanel(): Tf2HudModelPanel | null
  materialFrame(): import("@playsrc/rendering").HudMaterialFrame | undefined
  snapshot(): Tf2HudIntegrationSnapshot
  setPlayerClassUsePlayerModel(value: boolean): void
  setCrosshair(value: Tf2HudCrosshair): void
  setDeathNoticeTime(seconds: number): void
  setScoreboardVisibility(visible: boolean): void
  reset(reason: "map-replaced" | "disconnect"): void
  destroy(): void
}>

export type Tf2HudIntegrationRequest = Readonly<{
  root: HTMLElement
  resources: Tf2VguiResources
  viewport: VguiViewport
  reducedMotion: boolean
  clock: Readonly<{ nowSeconds(): number }>
  random: Readonly<{ nextUnit(): number }>
  damageIndicator?: Tf2DamageIndicatorInput
  onCommand(command: Tf2HudCommand): void
}>

const LIMITS: VguiRuntimeLimits = Object.freeze({
  maxPanels: 512,
  maxHierarchyDepth: 32,
  maxChildrenPerPanel: 256,
  maxResourceNodes: 2_048,
  maxResourceDepth: 32,
  maxPropertiesPerPanel: 256,
  maxStringCodeUnits: 4_095,
  maxTextCodeUnits: 65_535,
  maxDialogVariables: 256,
  maxLocalizationTokens: 4_096,
  maxSchemeColors: 1_024,
  maxSchemeSettings: 2_048,
  maxSchemeBorders: 512,
  maxSchemeImages: 2_048,
  maxAnimationScripts: 16,
  maxAnimationSequences: 1_024,
  maxAnimationCommands: 8_192,
  maxActiveAnimations: 2_048,
  maxDelayedCommands: 2_048,
  maxQueuedMessages: 2_048,
  maxDiagnostics: 2_048,
  maxDomNodes: 2_048,
  maxListeners: 64,
})

const HUD_LAYOUT = "scripts/hudlayout.res"
const HUD_CLASS = "resource/ui/hudplayerclass.res"
const HUD_HEALTH = "resource/ui/hudplayerhealth.res"
const HUD_AMMO = "resource/ui/hudammoweapons.res"
const HUD_WEAPONS = "resource/ui/hudweaponselection.res"
const HUD_MATCH_STATUS = "resource/ui/hudmatchstatus.res"
const HUD_TIME_PANEL = "resource/ui/hudobjectivetimepanel.res"
const HUD_KOTH_TIME_PANEL = "resource/ui/hudobjectivekothtimepanel.res"
const NO_ROUND_EVENTS: RoundSnapshot["events"] = Object.freeze([])
const HUD_WAITING_PANEL = "resource/ui/waitingforplayerspanel.res"
const HUD_SCOREBOARD = "resource/ui/scoreboard.res"
const HUD_OBJECTIVE_FLAGS = "resource/ui/hudobjectiveflagpanel.res"
const HUD_FLAG_STATUS = "resource/ui/flagstatus.res"
const HUD_WIN_PANEL = "resource/ui/winpanel.res"
const HUD_NOTIFICATION_BASE = "resource/ui/notifications/base_notification.res"
const FLAG_STATUS_IMAGES = Object.freeze([
  "../hud/objectives_flagpanel_ico_flag_home",
  "../hud/objectives_flagpanel_ico_flag_moving",
  "../hud/objectives_flagpanel_ico_flag_dropped",
])
const NOTIFICATION_FILES = Object.freeze([
  "your_flag_taken", "your_flag_dropped", "your_flag_returned", "your_flag_captured",
  "enemy_flag_taken", "enemy_flag_dropped", "enemy_flag_returned", "enemy_flag_captured",
  "touching_enemy_ctf_cap",
])
const HUD_SPY_METER = "resource/ui/huditemeffectmeter_spy.res"
const HUD_SPY_DISGUISE_MENU = "resource/ui/disguise_menu/hudmenuspydisguise.res"
const HUD_MEDIC_CHARGE = "resource/ui/hudmediccharge.res"
const HUD_DEMOMAN_PIPES = "resource/ui/huddemomanpipes.res"
const HUD_DEMOMAN_CHARGE = "resource/ui/huddemomancharge.res"
const scalar = (node: VguiResourceNode, name: string): string | null =>
  node.children.find((child) => child.name.toLowerCase() === name.toLowerCase() && child.value !== null)?.value ?? null
const node = (name: string, children: readonly VguiResourceNode[]): VguiResourceNode => Object.freeze({ name, value: null, condition: null, children: Object.freeze(children) })
const document = (source: VguiResourceDocument, suffix: string, root: VguiResourceNode): VguiResourceDocument => Object.freeze({
  logicalIdentity: `${source.logicalIdentity}/${suffix}`.toLowerCase(),
  revision: source.revision,
  root,
})

function apply(runtime: VguiRuntime, operation: VguiOperation): VguiPanelId | undefined {
  const result = runtime.apply(operation)
  if (!result.ok) throw new Error(`${result.diagnostic.code}:${result.diagnostic.subject}`)
  return result.panel
}

function find(runtime: VguiRuntime, name: string, parent?: VguiPanelId): VguiPanelId | null {
  return runtime.snapshot().panels.find((panel) => panel.name.toLowerCase() === name.toLowerCase() && (parent === undefined || panel.parent === parent))?.id ?? null
}

function resourceChildren(block: VguiResourceNode): readonly VguiResourceNode[] {
  return block.children.filter((child) => child.value === null && scalar(child, "ControlName") !== null)
}

function shallow(block: VguiResourceNode, conditions: readonly string[] = []): VguiResourceNode {
  return Object.freeze({ ...block, children: Object.freeze(block.children.filter((child) => child.value !== null
    || child.name.toLowerCase() === "controlname"
    || conditions.some((condition) => condition.toLowerCase() === child.name.toLowerCase()))) })
}

function applyChildren(runtime: VguiRuntime, parent: VguiPanelId, source: VguiResourceDocument, blocks: readonly VguiResourceNode[], selection: VguiResourceSelection): void {
  const selected = blocks.filter((block) => block.value === null && scalar(block, "ControlName") !== null)
  if (selected.length === 0) return
  apply(runtime, {
    kind: "replace-resource",
    parent,
    document: document(source, `children-${parent}`, node(source.root.name, selected.map((block) => shallow(block, selection.activeConditions)))),
    selection,
  })
  for (const block of selected) {
    const child = find(runtime, scalar(block, "fieldName") ?? block.name, parent)
    if (child !== null) applyChildren(runtime, child, source, resourceChildren(block), selection)
  }
}

function applyPanelResource(runtime: VguiRuntime, panel: VguiPanelId, source: VguiResourceDocument, selection: VguiResourceSelection): void {
  const snapshot = runtime.snapshot().panels.find((candidate) => candidate.id === panel)!
  const self = source.root.children.find((block) => (scalar(block, "fieldName") ?? block.name).toLowerCase() === snapshot.name.toLowerCase())
  if (self && snapshot.parent !== null) {
    apply(runtime, {
      kind: "replace-resource",
      parent: snapshot.parent,
      document: document(source, `self-${panel}`, node(source.root.name, [shallow(self, selection.activeConditions)])),
      selection,
    })
  }
  applyChildren(runtime, panel, source, source.root.children.filter((block) => block !== self), selection)
}

class Integration implements Tf2HudIntegration {
  readonly #countMeters = new Map<"kills" | "crits" | "heads", Readonly<{ root: VguiPanelId; background: VguiPanelId }>>()
  readonly #runtime: VguiRuntime
  readonly #crosshair: Tf2HudCrosshairPresentation
  readonly #scope: Tf2HudScopePresentation
  readonly #damage?: Tf2HudDamagePresentation
  readonly #clock: Readonly<{ nowSeconds(): number }>
  readonly #resources: Tf2VguiResources
  readonly #onCommand: (command: Tf2HudCommand) => void
  readonly #cloakLabel: string | undefined
  readonly #diagnostics: Tf2HudIntegrationDiagnostic[] = []
  readonly #diagnosticSubjects = new Set<string>()
  readonly #animationTrace: string[] = []
  readonly #baseBounds = new Map<string, Readonly<{ x: number; y: number; width: number; height: number }>>()
  readonly #panels = new Map<string, VguiPanelId>()
  readonly #publishedValues = new Map<string, string>()
  readonly #localization: ReadonlyMap<string, string>
  readonly #scoreboardProperties: ReadonlyMap<string, string>
  #publishedScoreboard?: Tf2HudScoreboard
  #modelPanel: Tf2HudModelPanel | null = null
  #modelPanelFingerprint = ""
  readonly #deathNotices: Tf2HudDeathNoticePresentation
  #previous: Tf2HudAvailability<Tf2HudSnapshot> = tf2HudUnavailable("initial")
  #binding: Tf2HudBinding | null = null
  #viewport: VguiViewport
  #objective?: Readonly<{
    root: VguiPanelId
    panel: VguiPanelId
    redFlag: VguiPanelId
    blueFlag: VguiPanelId
    redStatus: VguiPanelId
    blueStatus: VguiPanelId
    redArrow: VguiPanelId
    blueArrow: VguiPanelId
    carried: VguiPanelId
    outline: VguiPanelId
    captureArrow: VguiPanelId
    playingTo: VguiPanelId
    playingToBackground: VguiPanelId
    notification: VguiPanelId
    notificationBackground: VguiPanelId
    notificationLabel: VguiPanelId
  }>
  #medicPanels?: Readonly<{
    root: VguiPanelId
    meter: VguiPanelId | null
    background: VguiPanelId | null
    hidden: readonly VguiPanelId[]
  }>
  #notificationDeadline = 0n
  #objectiveCarrying = false
  #waitingPanelVisible = false
  #waitingPanelEnding = false
  #winPanel?: VguiPanelId
  #winPanels?: ReadonlyMap<string, VguiPanelId>
  #roundPanels?: Readonly<{
    match: VguiPanelId
    background: VguiPanelId
    timer: VguiPanelId
    value: VguiPanelId
    waiting: VguiPanelId
    timerChildren: ReadonlyMap<string, VguiPanelId>
    waitingChildren: ReadonlyMap<string, VguiPanelId>
  }>
  #kothPanels?: Readonly<{ root: VguiPanelId; timers: readonly Readonly<{ root: VguiPanelId; value: VguiPanelId; children: ReadonlyMap<string, VguiPanelId> }>[] }>
  #kothOvertime = [false, false]
  #kothActive = -1
  readonly #timerDeltas = new Map<VguiPanelId, TimerDeltas>()
  #lastRoundEventTick = -1n
  #destroyed = false
  #controlPoints?: ControlPointHud
  #demomanPanels!: Readonly<{
    pipes: VguiPanelId; present: VguiPanelId; absent: VguiPanelId;
    shieldLabel: VguiPanelId; shieldMeter: VguiPanelId;
    charge: VguiPanelId; chargeMeter: VguiPanelId;
    images: readonly Readonly<{ panel: VguiPanelId; red: string; blue: string }>[];
  }>
  #pipeTick: number
  #lastPipeCount = -1
  #demoman: Readonly<{ pipesVisible: boolean; chargeVisible: boolean; count: number; progress: number | null }> | null = null

  constructor(request: Tf2HudIntegrationRequest) {
    this.#resources = request.resources
    this.#clock = request.clock
    this.#pipeTick = Math.floor(request.clock.nowSeconds() * 1000) + 100
    this.#onCommand = request.onCommand
    this.#cloakLabel = request.resources.localization.tokens.find(token => token.name.toLowerCase() === "tf_cloak")?.value
    this.#viewport = Object.freeze({ ...request.viewport })
    this.#localization = new Map(request.resources.localization.tokens.map((token) => [`#${token.name.replace(/^#/u, "").toLowerCase()}`, token.value]))
    const scoreboardDocument = request.resources.document(HUD_SCOREBOARD)
    const scoreboardRoot = scoreboardDocument.root.children.find((block) => (scalar(block, "fieldName") ?? block.name).toLowerCase() === "scoreinfo")
    if (!scoreboardRoot) throw new Error("TF2 scoreboard authored root is unavailable")
    this.#scoreboardProperties = new Map(scoreboardRoot.children.filter((child) => child.value !== null).map((child) => [child.name.toLowerCase(), child.value!]))
    const availableImages = new Set(request.resources.clientScheme.images.map((image) => image.name.toLowerCase()))
    const missingImages = [...TF2_HUD_DYNAMIC_IMAGES, ...TF2_SCOREBOARD_IMAGES].filter((image) => !availableImages.has(image.toLowerCase()))
    if (missingImages.length > 0) throw new Error(`TF2 HUD dynamic images are unavailable: ${missingImages.join(",")}`)
    const initialized = initializeVguiRuntime({
      runtimeIdentity: "tf2-hud",
      root: request.root,
      rootControl: { control: "EditablePanel", name: "HudViewport" },
      viewport: request.viewport,
      limits: LIMITS,
      clock: request.clock,
      random: request.random,
      scheme: request.resources.clientScheme,
      localization: request.resources.localization,
      animationScripts: request.resources.animations,
      customControls: request.resources.customControls,
      reducedMotion: request.reducedMotion,
      onRequest: () => {},
    })
    if (!initialized.ok) throw new Error(`${initialized.diagnostic.code}:${initialized.diagnostic.subject}`)
    this.#runtime = initialized.runtime
    const selection = { activeConditions: request.resources.activeConditions, resolutionSuffixes: request.resources.resolutionSuffixes }
    this.#runtime.deferPresentation(() => {
    apply(this.#runtime, { kind: "set-panel-state", panel: 1, proportional: true, mouseInput: false, keyboardInput: false })
    const roots = [
      ["HudPlayerStatus", "CTFHudElement"],
      ["HudWeaponAmmo", "CTFHudElement"],
      ["HudWeaponSelection", "CTFHudElement"],
      ["HudItemEffectMeter", "CTFHudElement"],
      ["HudMenuSpyDisguise", "CTFHudElement"],
      ["HudCrosshair", "CTFHudElement"],
      ["HudDeathNotice", "CTFHudElement"],
      ["HudMedicCharge", "CTFHudElement"],
      ["HudDemomanPipes", "CTFHudElement"],
      ["HudDemomanCharge", "CTFHudElement"],
    ] as const
    for (const [name, control] of roots) apply(this.#runtime, { kind: "create-panel", parent: 1, control, name })
    const layout = request.resources.document(HUD_LAYOUT)
    const selectedLayout = layout.root.children.filter((block) => roots.some(([name]) => name === (scalar(block, "fieldName") ?? block.name)))
    apply(this.#runtime, {
      kind: "replace-resource",
      parent: 1,
      document: document(layout, "selected", node(layout.root.name, selectedLayout.map((block) => shallow(block)))),
      selection,
    })
    const status = find(this.#runtime, "HudPlayerStatus")!
    const playerClass = apply(this.#runtime, { kind: "create-panel", parent: status, control: "CTFHudElement", name: "HudPlayerClass" })!
    const playerHealth = apply(this.#runtime, { kind: "create-panel", parent: status, control: "CTFHudElement", name: "HudPlayerHealth" })!
    apply(this.#runtime, { kind: "create-panel", parent: playerHealth, control: "CTFHealthPanel", name: "PlayerStatusHealthImage" })
    applyPanelResource(this.#runtime, playerClass, request.resources.document(HUD_CLASS), selection)
    applyPanelResource(this.#runtime, playerHealth, request.resources.document(HUD_HEALTH), selection)
    applyPanelResource(this.#runtime, find(this.#runtime, "HudWeaponAmmo")!, request.resources.document(HUD_AMMO), selection)
    applyPanelResource(this.#runtime, find(this.#runtime, "HudWeaponSelection")!, request.resources.document(HUD_WEAPONS), selection)
    const scoreboard = apply(this.#runtime, { kind: "create-panel", parent: 1, control: "CTFClientScoreBoardDialog", name: "scoreinfo" })!
    const scoreboardNames = new Set<string>()
    const normalizedBlocks = scoreboardDocument.root.children.map((block) => {
      if (block.value !== null) return block
      const identity = (scalar(block, "fieldName") ?? block.name).toLowerCase()
      if (!scoreboardNames.has(identity)) {
        scoreboardNames.add(identity)
        return identity === "scoreinfo" && block.name.toLowerCase() !== identity
          ? Object.freeze({ ...block, name: "scoreinfo" })
          : block
      }
      return Object.freeze({
        ...block,
        children: Object.freeze(block.children.map((child) => child.name.toLowerCase() === "fieldname"
          ? Object.freeze({ ...child, value: block.name })
          : child)),
      })
    })
    applyPanelResource(this.#runtime, scoreboard,
      document(scoreboardDocument, "authored", node(scoreboardDocument.root.name, normalizedBlocks)),
      selection)
    apply(this.#runtime, { kind: "set-panel-state", panel: scoreboard, visible: false, z: 100 })
    for (const name of ["LocalPlayerDuelStatsPanel", "ButtonLegend", "ButtonLegendBG", "MvMScoreboard"]) {
      const panel = find(this.#runtime, name)
      if (panel !== null) apply(this.#runtime, { kind: "set-panel-state", panel, visible: false })
    }
    applyPanelResource(this.#runtime, find(this.#runtime, "HudItemEffectMeter")!, request.resources.document(HUD_SPY_METER), selection)
    for (const [kind, resource, token] of [
      ["kills", "resource/ui/huditemeffectmeter_demoman.res", "#tf_kills"],
      ["crits", "resource/ui/huditemeffectmeter_engineer.res", "#tf_crits"],
      ["heads", "resource/ui/huditemeffectmeter_sniper.res", "#tf_berzerk"],
    ] as const) {
      const name = `HudWeaponCount_${kind}`
      const root = apply(this.#runtime, { kind: "create-panel", parent: 1, control: "CTFHudElement", name })!
      const source = request.resources.document(resource)
      // Resource-local names remain authored. Only the viewport instance's
      // identity changes, so independent meters never bind each other's children.
      const blocks = source.root.children.map(block => block.name === "HudItemEffectMeter"
        ? Object.freeze({ ...block, name, children: Object.freeze(block.children.map(child => child.name === "fieldName" ? Object.freeze({ ...child, value: name }) : child)) }) : block)
      applyPanelResource(this.#runtime, root, document(source, kind, node(source.root.name, blocks)), selection)
      const label = find(this.#runtime, "ItemEffectMeterLabel", root)
      const background = find(this.#runtime, "ItemEffectMeterBG", root)
      const text = this.#localization.get(token)
      if (label === null || background === null || !text) throw new Error(`Authored TF2 count meter unavailable: ${resource}`)
      apply(this.#runtime, { kind: "mutate-control", panel: label, mutation: { text } })
      apply(this.#runtime, { kind: "set-panel-state", panel: root, visible: false })
      this.#countMeters.set(kind, Object.freeze({ root, background }))
    }
    applyPanelResource(this.#runtime, find(this.#runtime, "HudMenuSpyDisguise")!, request.resources.document(HUD_SPY_DISGUISE_MENU), selection)
    applyPanelResource(this.#runtime, find(this.#runtime, "HudMedicCharge")!, request.resources.document(HUD_MEDIC_CHARGE), selection)
    const pipes = find(this.#runtime, "HudDemomanPipes")!
    const charge = find(this.#runtime, "HudDemomanCharge")!
    const images: Array<{ panel: VguiPanelId; red: string; blue: string }> = []
    for (const [root, resource] of [[pipes, HUD_DEMOMAN_PIPES], [charge, HUD_DEMOMAN_CHARGE]] as const) {
      const source = request.resources.document(resource)
      applyPanelResource(this.#runtime, root, source, selection)
      const collect = (blocks: readonly VguiResourceNode[], parent: VguiPanelId) => {
        for (const block of blocks.filter(block => block.value === null && scalar(block, "ControlName") !== null)) {
          const panel = find(this.#runtime, scalar(block, "fieldName") ?? block.name, parent)
          if (panel === null) throw new Error(`TF2 Demoman HUD panel is unavailable: ${block.name}`)
          const red = scalar(block, "teambg_2"), blue = scalar(block, "teambg_3")
          if (red && blue) images.push({ panel, red, blue })
          collect(resourceChildren(block), panel)
        }
      }
      collect(source.root.children, root)
      apply(this.#runtime, { kind: "set-panel-state", panel: root, visible: false })
    }
    const child = (name: string, parent: VguiPanelId) => {
      const panel = find(this.#runtime, name, parent)
      if (panel === null) throw new Error(`TF2 Demoman HUD panel is unavailable: ${name}`)
      return panel
    }
    this.#demomanPanels = Object.freeze({ pipes, charge, present: child("PipesPresentPanel", pipes), absent: child("NoPipesPresentPanel", pipes),
      shieldLabel: child("ChargeLabel", pipes), shieldMeter: child("ChargeMeter", pipes), chargeMeter: child("ChargeMeter", charge), images })
    const panels = this.#runtime.snapshot().panels
    for (const panel of panels) {
      if (!this.#panels.has(panel.name.toLowerCase())) this.#panels.set(panel.name.toLowerCase(), panel.id)
      apply(this.#runtime, { kind: "set-panel-state", panel: panel.id, mouseInput: false, keyboardInput: false })
    }
    const medicRoot = this.#panels.get("hudmediccharge")
    if (medicRoot !== undefined) {
      const child = (name: string) => panels.find((panel) => panel.parent === medicRoot && panel.name.toLowerCase() === name.toLowerCase())?.id ?? null
      this.#medicPanels = Object.freeze({
        root: medicRoot,
        meter: child("ChargeMeter"),
        background: child("Background"),
        hidden: Object.freeze(["IndividualChargesLabel", "ChargeMeter1", "ChargeMeter2", "ChargeMeter3", "ChargeMeter4", "ResistIcon"]
          .map(child).filter((panel): panel is VguiPanelId => panel !== null)),
      })
    }
    this.#captureBaseBounds(panels)
    })
    this.#crosshair = new Tf2HudCrosshairPresentation(request.root)
    this.#deathNotices = new Tf2HudDeathNoticePresentation(request.root, request.resources, request.viewport, this.#runtime, find(this.#runtime, "HudDeathNotice")!)
    this.#scope = new Tf2HudScopePresentation(request.root)
    if (request.damageIndicator) this.#damage = new Tf2HudDamagePresentation(request.root, request.damageIndicator)
  }

  #initializeObjectives() {
    if (this.#objective) return this.#objective
    const selection = { activeConditions: this.#resources.activeConditions, resolutionSuffixes: this.#resources.resolutionSuffixes }
    const root = apply(this.#runtime, { kind: "create-panel", parent: 1, control: "CTFHudElement", name: "HudObjectiveStatus" })!
    const notification = apply(this.#runtime, { kind: "create-panel", parent: 1, control: "CTFHudElement", name: "NotificationPanel" })!
    const layout = this.#resources.document(HUD_LAYOUT)
    const selected = layout.root.children.filter((block) => ["HudObjectiveStatus", "NotificationPanel"].includes(scalar(block, "fieldName") ?? block.name))
    apply(this.#runtime, {
      kind: "replace-resource",
      parent: 1,
      document: document(layout, "ctf-objectives", node(layout.root.name, selected.map((block) => shallow(block)))),
      selection,
    })
    const panel = apply(this.#runtime, { kind: "create-panel", parent: root, control: "EditablePanel", name: "ObjectiveStatusFlagPanel" })!
    applyPanelResource(this.#runtime, panel, this.#resources.document(HUD_OBJECTIVE_FLAGS), selection)
    const redFlag = find(this.#runtime, "RedFlag", panel)!
    const blueFlag = find(this.#runtime, "BlueFlag", panel)!
    const flagDocument = this.#resources.document(HUD_FLAG_STATUS)
    applyPanelResource(this.#runtime, redFlag, flagDocument, selection)
    applyPanelResource(this.#runtime, blueFlag, flagDocument, selection)
    applyPanelResource(this.#runtime, notification, this.#resources.document(HUD_NOTIFICATION_BASE), selection)
    const required = (name: string, parent: VguiPanelId): VguiPanelId => {
      const value = find(this.#runtime, name, parent)
      if (value === null) throw new Error(`TF2 CTF HUD panel ${name} is missing`)
      return value
    }
    const redStatus = required("StatusIcon", redFlag), blueStatus = required("StatusIcon", blueFlag)
    const redArrow = required("Arrow", redFlag), blueArrow = required("Arrow", blueFlag)
    apply(this.#runtime, { kind: "mutate-control", panel: redArrow, mutation: { image: "../hud/objectives_flagpanel_compass_red" } })
    apply(this.#runtime, { kind: "mutate-control", panel: blueArrow, mutation: { image: "../hud/objectives_flagpanel_compass_blue" } })
    apply(this.#runtime, { kind: "set-panel-state", panel: notification, visible: false, mouseInput: false, keyboardInput: false })
    const objective = Object.freeze({
      root, panel, redFlag, blueFlag, redStatus, blueStatus, redArrow, blueArrow,
      carried: required("CarriedImage", panel),
      outline: required("OutlineImage", panel),
      captureArrow: required("CaptureFlag", panel),
      playingTo: required("PlayingTo", panel),
      playingToBackground: required("PlayingToBG", panel),
      notification,
      notificationBackground: required("Notification_Background", notification),
      notificationLabel: required("Notification_Label", notification),
    })
    for (const value of this.#runtime.snapshot().panels) {
      if (!this.#panels.has(value.name.toLowerCase())) this.#panels.set(value.name.toLowerCase(), value.id)
      apply(this.#runtime, { kind: "set-panel-state", panel: value.id, mouseInput: false, keyboardInput: false })
    }
    apply(this.#runtime, { kind: "set-panel-state", panel: objective.outline, visible: false })
    this.#captureBaseBounds()
    this.#objective = objective
    return objective
  }

  #initializeRoundPanels() {
    if (this.#roundPanels) return this.#roundPanels
    const selection = { activeConditions: this.#resources.activeConditions, resolutionSuffixes: this.#resources.resolutionSuffixes }
    const matchSelection = { ...selection, activeConditions: Object.freeze([...selection.activeConditions, "if_match"]) }
    const match = apply(this.#runtime, { kind: "create-panel", parent: 1, control: "CTFHudElement", name: "HudMatchStatus" })!
    const waiting = apply(this.#runtime, { kind: "create-panel", parent: 1, control: "CTFHudElement", name: "WaitingForPlayersPanel" })!
    const layout = this.#resources.document(HUD_LAYOUT)
    const roots = layout.root.children.filter((value) => ["HudMatchStatus", "WaitingForPlayersPanel"].includes(scalar(value, "fieldName") ?? value.name))
    if (roots.length !== 2) throw new Error("TF2 authored round HUD root layout is unavailable")
    apply(this.#runtime, {
      kind: "replace-resource", parent: 1,
      document: document(layout, "round-roots", node(layout.root.name, roots.map((block) => shallow(block)))),
      selection,
    })
    const timer = apply(this.#runtime, { kind: "create-panel", parent: match, control: "CTFHudTimeStatus", name: "ObjectiveStatusTimePanel" })!
    const background = apply(this.#runtime, { kind: "create-panel", parent: match, control: "EditablePanel", name: "BGFrame" })!
    const matchResource = this.#resources.document(HUD_MATCH_STATUS)
    const authoredTimer = matchResource.root.children.find((value) => (scalar(value, "fieldName") ?? value.name) === "ObjectiveStatusTimePanel")
    const authoredBackground = matchResource.root.children.find((value) => (scalar(value, "fieldName") ?? value.name) === "BGFrame")
    if (!authoredTimer || !authoredBackground) throw new Error("TF2 authored round timer position is unavailable")
    apply(this.#runtime, {
      kind: "replace-resource", parent: match,
      document: document(matchResource, "round-timer-position", node(matchResource.root.name,
        [shallow(authoredTimer, matchSelection.activeConditions), shallow(authoredBackground, matchSelection.activeConditions)])),
      selection: matchSelection,
    })
    const value = apply(this.#runtime, { kind: "create-panel", parent: timer, control: "CExLabel", name: "TimePanelValue" })!
    applyChildren(this.#runtime, timer, matchResource, resourceChildren(authoredTimer), matchSelection)
    applyPanelResource(this.#runtime, timer, this.#resources.document(HUD_TIME_PANEL), matchSelection)
    applyPanelResource(this.#runtime, waiting, this.#resources.document(HUD_WAITING_PANEL), selection)
    const timerChildren = new Map<string, VguiPanelId>()
    const waitingChildren = new Map<string, VguiPanelId>()
    for (const panel of this.#runtime.snapshot().panels) {
      if (!this.#panels.has(panel.name.toLowerCase())) this.#panels.set(panel.name.toLowerCase(), panel.id)
      if (panel.parent === timer) timerChildren.set(panel.name.toLowerCase(), panel.id)
      if (panel.parent === waiting) waitingChildren.set(panel.name.toLowerCase(), panel.id)
      apply(this.#runtime, { kind: "set-panel-state", panel: panel.id, mouseInput: false, keyboardInput: false })
    }
    this.#captureBaseBounds()
    this.#roundPanels = Object.freeze({ match, background, timer, value, waiting, timerChildren, waitingChildren })
    return this.#roundPanels
  }

  #publishRound(round: RoundSnapshot, team: Tf2Team): void {
    this.#publishKoth(round)
    if (!round.waitingForPlayers && !round.timer && !this.#roundPanels) return
    const panels = this.#initializeRoundPanels()
    for (const event of round.events) {
      if (event.kind === 5) for (const delta of this.#timerDeltas.values()) delta.reset()
      if (event.kind !== 16 || event.value === 0) continue
      const kothIndex = round.kothTimers?.findIndex(timer => timer.identity === event.identity) ?? -1
      const root = kothIndex >= 0 ? this.#kothPanels?.timers[kothIndex]?.root
        : round.timer?.identity === event.identity ? panels.timer : undefined
      if (root === undefined) continue
      let deltas = this.#timerDeltas.get(root)
      if (!deltas) {
        deltas = new TimerDeltas(this.#runtime, this.#runtime.snapshot().panels.find(panel => panel.id === root)!)
        this.#timerDeltas.set(root, deltas)
      }
      deltas.add(event.value, this.#clock.nowSeconds())
    }
    const timerVisible = round.waitingForPlayers || (!round.kothTimers && round.timer !== null && round.timer.showInHud && !round.timer.disabled)
    const setVisible = (panel: VguiPanelId, visible: boolean, name: string): void => {
      this.#objectiveValue(`round-visible:${name}`, String(visible), { kind: "set-panel-state", panel, visible })
    }
    const matchVisible = timerVisible || round.kothTimers !== null
    setVisible(panels.match, matchVisible, "match")
    setVisible(panels.background, matchVisible, "match-background")
    setVisible(panels.timer, timerVisible, "timer")
    for (const event of round.events) {
      if (event.kind === 2) { this.#waitingPanelVisible = true; this.#waitingPanelEnding = false }
      else if (event.kind === 3) { this.#waitingPanelVisible = true; this.#waitingPanelEnding = true }
      else if (event.kind === 4) { this.#waitingPanelVisible = false; this.#waitingPanelEnding = false }
    }
    if (!round.waitingForPlayers) { this.#waitingPanelVisible = false; this.#waitingPanelEnding = false }
    setVisible(panels.waiting, this.#waitingPanelVisible, "waiting")
    const seconds = Math.max(0, Math.floor(round.waitingForPlayers ? round.waitingRemaining ?? 0 : round.timer?.remaining ?? 0))
    const text = `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`
    this.#objectiveValue("round-time", text, { kind: "mutate-control", panel: panels.value, mutation: { text } })
    const background = panels.timerChildren.get("timepanelbg")
    if (background !== undefined) {
      const image = `../hud/objectives_timepanel_${team === 2 ? "red" : "blue"}_bg`
      this.#objectiveValue("round-team-background", image, { kind: "mutate-control", panel: background, mutation: { image } })
    }
    for (const [name, visible] of [
      ["WaitingForPlayersLabel", round.waitingForPlayers],
      ["WaitingForPlayersBG", round.waitingForPlayers],
      ["SetupLabel", round.inSetup && !round.waitingForPlayers],
      ["SetupBG", round.inSetup && !round.waitingForPlayers],
      ["OvertimeLabel", round.inOvertime],
      ["OvertimeBG", round.inOvertime],
      ["SuddenDeathLabel", round.state === 7],
      ["SuddenDeathBG", round.state === 7],
      ["ServerTimeLimitLabel", false],
      ["ServerTimeLimitLabelBG", false],
    ] as const) {
      const panel = panels.timerChildren.get(name.toLowerCase())
      if (panel !== undefined) setVisible(panel, visible, `timer-${name}`)
    }
    for (const [name, visible] of [
      ["WaitingForPlayersLabel", !this.#waitingPanelEnding],
      ["WaitingForPlayersEndingLabel", this.#waitingPanelEnding],
    ] as const) {
      const panel = panels.waitingChildren.get(name.toLowerCase())
      if (panel !== undefined) setVisible(panel, visible, `waiting-${name}`)
    }
  }

  #publishKoth(round: RoundSnapshot): void {
    if (!round.kothTimers && !this.#kothPanels) return
    if (!this.#kothPanels) {
      const selection = { activeConditions: Object.freeze([...this.#resources.activeConditions, "if_match"]), resolutionSuffixes: this.#resources.resolutionSuffixes }
      const root = apply(this.#runtime, { kind: "create-panel", parent: 1, control: "CTFHudElement", name: "HudKothTimeStatus" })!
      const layout = this.#resources.document(HUD_LAYOUT)
      const authoredRoot = layout.root.children.find(value => (scalar(value, "fieldName") ?? value.name) === "HudKothTimeStatus")
      if (!authoredRoot) throw new Error("TF2 authored KOTH root is unavailable")
      apply(this.#runtime, { kind: "replace-resource", parent: 1, document: document(layout, "koth-root", node(layout.root.name, [shallow(authoredRoot)])), selection })
      const resource = this.#resources.document(HUD_KOTH_TIME_PANEL)
      const timers = ["RedTimer", "BlueTimer"].map(name => {
        const timer = apply(this.#runtime, { kind: "create-panel", parent: root, control: "CTFHudTimeStatus", name })!
        const value = apply(this.#runtime, { kind: "create-panel", parent: timer, control: "CExLabel", name: "TimePanelValue" })!
        applyPanelResource(this.#runtime, timer, this.#resources.document(HUD_TIME_PANEL), selection)
        return { root: timer, value, children: new Map<string, VguiPanelId>() }
      })
      applyPanelResource(this.#runtime, root, resource, selection)
      // Child ApplySchemeSettings loads the common time panel, then reapplies
      // the per-clock settings saved by the KOTH parent.
      for (const timer of timers) {
        const name = timer === timers[0] ? "RedTimer" : "BlueTimer"
        const authored = resource.root.children.find(block => (scalar(block, "fieldName") ?? block.name) === name)!
        const common = this.#resources.document(HUD_TIME_PANEL)
        const merge = (base: VguiResourceNode, override: VguiResourceNode): VguiResourceNode => node(base.name, [
          ...base.children.filter(child => !override.children.some(value => value.name.toLowerCase() === child.name.toLowerCase())),
          ...override.children.map(child => {
            const previous = base.children.find(value => value.name.toLowerCase() === child.name.toLowerCase())
            return child.value === null && previous?.value === null ? merge(previous, child) : child
          }),
        ])
        const children = common.root.children.map(block => {
          const override = resourceChildren(authored).find(value => value.name.toLowerCase() === block.name.toLowerCase())
          return override ? merge(block, override) : block
        })
        applyChildren(this.#runtime, timer.root, common, children, selection)
      }
      for (const panel of this.#runtime.snapshot().panels) {
        for (const timer of timers) if (panel.parent === timer.root) timer.children.set(panel.name.toLowerCase(), panel.id)
        apply(this.#runtime, { kind: "set-panel-state", panel: panel.id, mouseInput: false, keyboardInput: false })
      }
      this.#kothPanels = Object.freeze({ root, timers })
      this.#captureBaseBounds()
    }
    const panels = this.#kothPanels
    const visible = round.kothTimers !== null && !round.waitingForPlayers
    this.#objectiveValue("koth-visible", String(visible), { kind: "set-panel-state", panel: panels.root, visible })
    if (!round.kothTimers) return
    if (round.events.some(event => event.kind === 5)) {
      for (const panel of panels.timers) apply(this.#runtime, { kind: "stop-animation-sequence", parent: panel.root, sequence: "OvertimeLabelPulseRed" })
      this.#kothOvertime = [false, false]
    }
    const active = !round.kothTimers[0].paused ? 2 : !round.kothTimers[1].paused ? 3 : 0
    for (let index = 0; index < 2; index++) {
      const timer = round.kothTimers[index]!, panel = panels.timers[index]!, team = index === 0 ? 2 : 3
      const seconds = Math.max(0, Math.floor(timer.remaining))
      const text = `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`
      this.#objectiveValue(`koth-time:${team}`, text, { kind: "mutate-control", panel: panel.value, mutation: { text } })
      const previousOvertime = this.#kothOvertime[index]
      if (timer.remaining > 0) this.#kothOvertime[index] = false
      else if (round.inOvertime) this.#kothOvertime[index] = true
      if (previousOvertime !== this.#kothOvertime[index]) {
        apply(this.#runtime, this.#kothOvertime[index]
          ? { kind: "start-animation-sequence", parent: panel.root, sequence: "OvertimeLabelPulseRed", cancelable: true }
          : { kind: "stop-animation-sequence", parent: panel.root, sequence: "OvertimeLabelPulseRed" })
      }
      for (const [name, show] of [
        ["OvertimeLabel", this.#kothOvertime[index]], ["OvertimeBG", this.#kothOvertime[index]],
        ["SetupLabel", false], ["SetupBG", false], ["WaitingForPlayersLabel", false], ["WaitingForPlayersBG", false],
        ["SuddenDeathLabel", false], ["SuddenDeathBG", false], ["ServerTimeLimitLabel", false], ["ServerTimeLimitLabelBG", false],
      ] as const) {
        const child = panel.children.get(name.toLowerCase())
        if (child !== undefined) this.#objectiveValue(`koth-${team}:${name}`, String(show), { kind: "set-panel-state", panel: child, visible: show })
      }
      const background = panel.children.get("timepanelbg")
      if (background !== undefined) {
        const image = `../hud/objectives_timepanel_${team === 2 ? "red" : "blue"}_bg`
        this.#objectiveValue(`koth-background:${team}`, image, { kind: "mutate-control", panel: background, mutation: { image } })
      }
      if (active !== this.#kothActive) apply(this.#runtime, { kind: "start-animation-sequence", parent: panel.root, sequence: active === team ? "ActiveTimerHighlight" : "ActiveTimerDim", cancelable: false })
    }
    this.#kothActive = active
  }

  #publishWinPanel(
    round: RoundSnapshot | undefined,
    objectives: CaptureObjectives | undefined,
    publication: SessionSimulationPublication,
    scoreboard: Tf2HudScoreboard | undefined,
  ): void {
    const winner = round?.winningTeam ?? objectives?.winner ?? null
    const draw = round?.state === 5 && round.winReason === 5
    if (winner === null && !draw) {
      if (this.#winPanel !== undefined) {
        this.#objectiveValue("ctf-win:visible", "false", { kind: "set-panel-state", panel: this.#winPanel, visible: false })
      }
      return
    }
    let panel = this.#winPanel
    if (panel === undefined) {
      panel = apply(this.#runtime, { kind: "create-panel", parent: 1, control: "CTFHudElement", name: "WinPanel" })!
      const layout = this.#resources.document(HUD_LAYOUT)
      const selected = layout.root.children.find((value) => (scalar(value, "fieldName") ?? value.name) === "WinPanel")
      if (!selected) throw new Error("TF2 authored round-win layout is unavailable")
      apply(this.#runtime, {
        kind: "replace-resource",
        parent: 1,
        document: document(layout, "ctf-win-panel", node(layout.root.name, [shallow(selected)])),
        selection: { activeConditions: this.#resources.activeConditions, resolutionSuffixes: this.#resources.resolutionSuffixes },
      })
      applyPanelResource(this.#runtime, panel, this.#resources.document(HUD_WIN_PANEL), {
        activeConditions: this.#resources.activeConditions,
        resolutionSuffixes: this.#resources.resolutionSuffixes,
      })
      this.#winPanel = panel
      const descendants = new Set<VguiPanelId>([panel])
      const winPanels = new Map<string, VguiPanelId>()
      for (const value of this.#runtime.snapshot().panels) {
        if (value.id === panel || (value.parent !== null && descendants.has(value.parent))) {
          descendants.add(value.id)
          winPanels.set(value.name.toLowerCase(), value.id)
        }
        if (!this.#panels.has(value.name.toLowerCase())) this.#panels.set(value.name.toLowerCase(), value.id)
        apply(this.#runtime, { kind: "set-panel-state", panel: value.id, mouseInput: false, keyboardInput: false })
      }
      this.#winPanels = winPanels
      this.#captureBaseBounds()
    }
    const winPanels = this.#winPanels
    if (winPanels === undefined) throw new Error("TF2 authored round-win panel inventory is unavailable")
    const background = winPanels.get("winpanelbgborder")
    if (background === undefined) throw new Error("TF2 authored round-win background is unavailable")
    const border = draw ? "TFFatLineBorder" : winner === 2 ? "TFFatLineBorderRedBG" : "TFFatLineBorderBlueBG"
    this.#objectiveValue("ctf-win:border", border, { kind: "mutate-control", panel: background, mutation: { border } })
    const alreadyVisible = this.#publishedValues.get("ctf-win:visible") === "true"
    this.#objectiveValue("ctf-win:visible", "true", { kind: "set-panel-state", panel, visible: true })
    const localized = (name: string): string => this.#localization.get(`#${name.replace(/^#/u, "").toLowerCase()}`) ?? name
    const team = winner === 2 ? "RED" : "BLU"
    const fullRound = round ? round.fullRound : true
    const winning = draw ? localized("#Winpanel_Stalemate") : localized(fullRound ? "#Winpanel_TeamWins" : round?.winReason === 4 ? "#Winpanel_TeamDefends" : "#Winpanel_TeamAdvances")
      .replace("%s1", team)
      .replace("%s2", localized("#Winpanel_Team1"))
    const reasonToken = round?.winReason === 1 ? "#Winreason_AllPointsCaptured"
      : round?.winReason === 4 ? "#Winreason_DefendedUntilTimeLimit"
      : round?.winReason === 2 ? "#Winreason_OpponentsDead"
        : round?.winReason === 5 ? "#Winreason_Stalemate"
          : objectives?.captureLimit === 1 ? "#Winreason_FlagCaptureLimit_One" : "#Winreason_FlagCaptureLimit"
    const reason = localized(reasonToken)
      .replace("%s1", team)
      .replace("%s2", String(objectives?.captureLimit ?? 0))
    const capture = objectives?.events.find((event) => event.kind === 2 && event.detail === 2 && event.player !== null)
    const capturingPlayer = scoreboard?.players.find((player) => player.identity === capture?.player)
    let winningCappers: string[] | undefined
    for (const event of round?.events ?? NO_ROUND_EVENTS) if (event.kind === 17) {
      (winningCappers ??= []).push(scoreboard?.players.find(player => player.identity === event.identity)?.name ?? "")
    }
    const details = winningCappers ? localized("#Winpanel_WinningCapture").replace("%s1", winningCappers.join(", "))
      : capturingPlayer ? localized("#Winpanel_WinningCapture").replace("%s1", capturingPlayer.name)
      : alreadyVisible ? this.#publishedValues.get("ctf-win:DetailsLabel") ?? "" : ""
    for (const [name, value] of [
      ["WinningTeamLabel", fullRound ? winning : ""],
      ["AdvancingTeamLabel", fullRound ? "" : winning],
      ["WinReasonLabel", reason],
      ["DetailsLabel", details],
      ["TopPlayersLabel", localized(draw ? "#Winpanel_TopPlayers" : winner === 2 ? "#Winpanel_RedMVPs" : "#Winpanel_BlueMVPs")],
    ] as const) {
      this.#objectiveValue(`ctf-win:${name}`, String(value), { kind: "set-dialog-variable", panel, name, value })
    }
    const scores = winPanels.get("teamscorespanel")
    if (scores === undefined) throw new Error("TF2 authored round-win team score panel is unavailable")
    for (const [name, value] of [
      ["redteamname", "RED"],
      ["blueteamname", "BLU"],
      ["redteamscore", round?.redScore ?? objectives?.redScore ?? 0],
      ["blueteamscore", round?.blueScore ?? objectives?.blueScore ?? 0],
    ] as const) {
      this.#objectiveValue(`ctf-win:${name}`, String(value), { kind: "set-dialog-variable", panel: scores, name, value })
    }
    for (const name of ["BlueLeaderAvatar", "BlueLeaderAvatarBG", "RedLeaderAvatar", "RedLeaderAvatarBG"]) {
      const avatar = winPanels.get(name.toLowerCase())
      if (avatar !== undefined) {
        this.#objectiveValue(`ctf-win:${name}:visible`, "false", { kind: "set-panel-state", panel: avatar, visible: false })
      }
    }
    const players = scoreboard?.players.filter((player) => (draw || player.team === winner) && player.score > 0).slice(0, 3) ?? []
    const classTokens = ["", "Scout", "Sniper", "Soldier", "Demoman", "Medic", "HWGuy", "Pyro", "Spy", "Engineer"] as const
    const updatePlayer = (prefix: string, player: Tf2ScoreboardPlayer | undefined, score: number): void => {
      const identity = player?.class.kind === "available" ? player.class.value
        : player?.identity === 1 ? publication.snapshot.class
          : publication.snapshot.bots?.find((bot) => bot.identity === player?.identity)?.class
      const values = [
        ["Name", player?.name ?? ""],
        ["Class", identity === undefined ? "" : localized(`#TF_Class_Name_${classTokens[identity]}`)],
        ["Score", player === undefined ? "" : String(score)],
      ] as const
      for (const [suffix, text] of values) {
        const name = `${prefix}${suffix}`
        const label = winPanels.get(name.toLowerCase())
        if (label === undefined) throw new Error(`TF2 authored round-win player label is unavailable: ${name}`)
        const color = player?.team === 2 ? [255, 64, 64, 255] : [153, 204, 255, 255]
        this.#objectiveValue(`ctf-win:${name}:value`, `${text}:${player?.team ?? 0}`, {
          kind: "mutate-control",
          panel: label,
          mutation: { text, foregroundColor: color as [number, number, number, number] },
        })
        this.#objectiveValue(`ctf-win:${name}:visible`, String(player !== undefined), {
          kind: "set-panel-state", panel: label, visible: player !== undefined,
        })
      }
      for (const [suffix, visible] of [["Avatar", player !== undefined], ["Badge", false]] as const) {
        const name = `${prefix}${suffix}`
        const value = winPanels.get(name.toLowerCase())
        if (value !== undefined) {
          this.#objectiveValue(`ctf-win:${name}:visible`, String(visible), { kind: "set-panel-state", panel: value, visible })
        }
      }
    }
    for (let index = 0; index < 3; index += 1) updatePlayer(`Player${index + 1}`, players[index], players[index]?.score ?? 0)
    const leader = scoreboard?.players.filter((player) => player.killstreak > 0)
      .toSorted((left, right) => right.killstreak - left.killstreak || left.identity - right.identity)[0]
    updatePlayer("KillStreakPlayer1", leader, leader?.killstreak ?? 0)
  }

  #objectiveValue(identity: string, value: string, operation: VguiOperation): void {
    if (this.#publishedValues.get(identity) === value) return
    apply(this.#runtime, operation)
    this.#publishedValues.set(identity, value)
  }

  #publishObjectives(objectives: CaptureObjectives, player: number, team: Tf2Team, tick: bigint): void {
    const panels = this.#initializeObjectives()
    const carried = objectives.flags.find((flag) => flag.carrier === player)
    const visible = (panel: VguiPanelId, value: boolean, name: string): void => {
      this.#objectiveValue(`ctf-visible:${name}`, String(value), { kind: "set-panel-state", panel, visible: value })
    }
    const image = (panel: VguiPanelId, value: string, name: string): void => {
      this.#objectiveValue(`ctf-image:${name}`, value, { kind: "mutate-control", panel, mutation: { image: value } })
    }
    visible(panels.root, true, "root")
    for (const flag of objectives.flags) {
      const red = flag.team === 2
      const status = red ? panels.redStatus : panels.blueStatus
      const panel = red ? panels.redFlag : panels.blueFlag
      const name = red ? "red" : "blue"
      visible(panel, !carried && (!flag.disabled || flag.visibleWhenDisabled), name)
      image(status, FLAG_STATUS_IMAGES[flag.status]!, `${name}-status`)
    }
    visible(panels.carried, carried !== undefined, "carried")
    visible(panels.captureArrow, carried !== undefined, "capture-arrow")
    if (this.#objectiveCarrying !== (carried !== undefined)) {
      this.#objectiveCarrying = carried !== undefined
      visible(panels.outline, true, "outline")
      try {
        apply(this.#runtime, { kind: "start-animation-sequence", parent: 1, sequence: "FlagOutline", cancelable: true })
        this.#animationTrace.push(`${tick}:ctf:viewport:FlagOutline`)
      } catch {
        this.#diagnostic("AnimationUnavailable", "viewport:FlagOutline")
      }
    }
    if (carried) {
      const enemy = team === 2 ? "blue" : "red"
      image(panels.carried, `${carried.icon}_${enemy}`, "carried")
      image(panels.captureArrow, `../hud/objectives_flagpanel_compass_${team === 2 ? "red" : "blue"}`, "capture-arrow")
    }
    const limited = objectives.captureLimit > 0
    visible(panels.playingTo, limited, "playing-to")
    visible(panels.playingToBackground, limited, "playing-to-background")
    for (const [name, value] of [
      ["redscore", limited ? objectives.redCaptures : objectives.redScore],
      ["bluescore", limited ? objectives.blueCaptures : objectives.blueScore],
      ["rounds", objectives.captureLimit],
    ] as const) {
      this.#objectiveValue(`ctf-variable:${name}`, String(value), { kind: "set-dialog-variable", panel: panels.panel, name, value })
    }
    for (const event of objectives.events) {
      if (event.kind !== 4 || event.team !== team || event.player === player) continue
      const filename = NOTIFICATION_FILES[event.detail]
      if (!filename) throw new Error(`TF2 CTF HUD notification ${event.detail} is invalid`)
      const resource = this.#resources.panelDocument(`resource/ui/notifications/notify_${filename}_${team === 2 ? "red" : "blue"}.res`)
      const root = resource.roots[0]
      const background = root?.children.find((child) => child.name === "Notification_Background")
      const label = root?.children.find((child) => child.name === "Notification_Label")
      const backgroundImage = background?.children.find((child) => child.name.toLowerCase() === "image")?.value
      const token = label?.children.find((child) => child.name.toLowerCase() === "labeltext")?.value
      if (!backgroundImage || !token) throw new Error(`TF2 CTF HUD notification ${filename} is incomplete`)
      image(panels.notificationBackground, backgroundImage, "notification-background")
      const localized = this.#resources.localization.tokens.find((item) => item.name.toLowerCase() === token.slice(1).toLowerCase())?.value ?? token
      this.#objectiveValue("ctf-notification-label", localized, { kind: "mutate-control", panel: panels.notificationLabel, mutation: { text: localized } })
      this.#notificationDeadline = tick + 200n
      visible(panels.notification, true, "notification")
    }
    if (tick >= this.#notificationDeadline) visible(panels.notification, false, "notification")
  }

  #diagnostic(code: Tf2HudIntegrationDiagnostic["code"], subject: string): void {
    const identity = `${code}:${subject}`
    if (this.#diagnosticSubjects.has(identity) || this.#diagnostics.length >= LIMITS.maxDiagnostics) return
    this.#diagnosticSubjects.add(identity)
    this.#diagnostics.push(Object.freeze({ code, subject }))
  }

  #captureBaseBounds(panels = this.#runtime.snapshot().panels): void {
    for (const panel of panels) this.#baseBounds.set(panel.name, panel.bounds)
  }

  #value(value: Tf2HudPanelValue): void {
    const panel = this.#panels.get(value.panel.toLowerCase()) ?? null
    if (panel === null) {
      this.#diagnostic("PanelUnavailable", `${value.kind}:${value.panel}`)
      return
    }
    const identity = value.kind === "dialog-variable"
      ? `${value.kind}:${value.panel}:${value.variable}`
      : value.kind === "scalar" || value.kind === "color"
        ? `${value.kind}:${value.panel}:${value.property}`
        : `${value.kind}:${value.panel}`
    const fingerprint = value.kind === "visible"
      ? String(value.value)
      : value.value.kind === "unavailable"
        ? `unavailable:${value.value.reason}`
        : typeof value.value.value !== "object"
          ? `${typeof value.value.value}:${value.value.value}`
          : Array.isArray(value.value.value)
            ? `color:${value.value.value.join(",")}`
            : `localized:${value.value.value.token}:${value.value.value.parameters.join("\u0000")}`
    if (this.#publishedValues.get(identity) === fingerprint) return
    if (value.kind === "visible") {
      apply(this.#runtime, { kind: "set-panel-state", panel, visible: value.value })
      this.#publishedValues.set(identity, fingerprint)
      return
    }
    if (value.value.kind === "unavailable") {
      this.#diagnostic("ValueUnavailable", `${value.kind}:${value.panel}:${value.value.reason}`)
      return
    }
    if (value.kind === "dialog-variable") {
      const available = value.value.value
      const rendered = typeof available === "object"
        ? (this.#localization.get(available.token.toLowerCase()) ?? available.token)
          .replace(/%s([1-9])/gu, (_match, index: string) => String(available.parameters[Number(index) - 1] ?? ""))
        : available
      if (value.panel === "ItemEffectMeterLabel" && value.variable === "labelText") {
        if (!this.#cloakLabel) throw new Error("Authored Spy cloak localization is unavailable")
        apply(this.#runtime, { kind: "mutate-control", panel, mutation: { text: this.#cloakLabel } })
      } else {
        apply(this.#runtime, { kind: "set-dialog-variable", panel, name: value.variable, value: rendered })
      }
    } else if (value.kind === "image") {
      try { apply(this.#runtime, { kind: "mutate-control", panel, mutation: { image: value.value.value } }) }
      catch { this.#diagnostic("ValueUnavailable", `image:${value.panel}:${value.value.value}`); return }
    } else if (value.kind === "color") {
      const color = Object.freeze([...value.value.value]) as readonly [number, number, number, number]
      apply(this.#runtime, { kind: "mutate-control", panel, mutation: value.property.toLowerCase() === "drawcolor" ? { drawColor: color } : { foregroundColor: color } })
    } else if (value.kind === "scalar") {
      if (value.property === "fill") {
        apply(this.#runtime, { kind: "mutate-control", panel, mutation: {
          imageFill: value.value.value,
          image: value.value.value <= 0 ? "hud/health_dead" : "hud/health_color",
        } })
      } else if (value.property === "progress") {
        apply(this.#runtime, { kind: "mutate-control", panel, mutation: { progress: value.value.value } })
      } else if (value.property === "boundsAdjustment") {
        const base = this.#baseBounds.get(value.panel)
        if (!base) {
          this.#diagnostics.push(Object.freeze({ code: "PanelUnavailable", subject: `bounds:${value.panel}` }))
          return
        }
        const amount = value.value.value
        apply(this.#runtime, { kind: "set-bounds", panel, bounds: { x: base.x - amount, y: base.y - amount, width: base.width + amount * 2, height: base.height + amount * 2 } })
      } else {
        apply(this.#runtime, { kind: "mutate-control", panel, mutation: { scalarProperties: { [value.property]: value.value.value } } })
      }
    }
    this.#publishedValues.set(identity, fingerprint)
  }

  #scoreboardColumnWidth(name: string): number {
    const value = Number(this.#scoreboardProperties.get(name))
    if (!Number.isSafeInteger(value) || value < 0) throw new Error(`TF2 scoreboard authored width is invalid: ${name}`)
    return Math.trunc(value * this.#viewport.height / 480)
  }

  #scoreboardPlayer(player: Tf2ScoreboardPlayer, pingAsText: boolean): VguiSectionedListItem {
    const dead = player.alive ? "" : "_d"
    const cells: Record<string, string | Readonly<{ kind: "image"; image: string }>> = {
      medal: "",
      avatar: "",
      spacer: "",
      name: player.connection === "connected"
        ? player.name
        : player.connection === "disconnected" ? "#TF_MM_PlayerLostConnection" : "#TF_MM_PlayerConnecting",
      killstreak: player.killstreak ? String(player.killstreak) : "",
      killstreak_image: "",
      dominating: "",
      nemesis: "",
      score: String(player.score),
      class: player.class.kind === "available"
        ? Object.freeze({ kind: "image", image: `${TF2_SCOREBOARD_CLASS_IMAGES[player.class.value - 1]}${dead}` })
        : "",
      ping: "",
    }
    if (player.ping.kind === "available") {
      const ping = player.ping.value
      if (pingAsText) cells.ping = ping === "bot" ? "#TF_Scoreboard_Bot" : ping > 0 ? String(ping) : ""
      else if (ping === "bot") cells.ping = Object.freeze({
        kind: "image",
        image: `../hud/scoreboard_ping_bot_${player.team === 2 ? "red" : "blue"}${dead}`,
      })
      else if (ping > 0) cells.ping = Object.freeze({
        kind: "image",
        image: `../hud/scoreboard_ping_${ping < 125 ? "low" : ping < 200 ? "med" : ping < 275 ? "high" : "very_high"}${dead}`,
      })
    }
    const local = player.identity === 1
    const foregroundColor = Object.freeze(player.alive
      ? player.team === 2 ? [255, 64, 64, 255] : [153, 204, 255, 255]
      : player.team === 2
        ? local ? [182, 75, 75, 255] : [135, 83, 83, 255]
        : local ? [123, 153, 187, 255] : [81, 97, 129, 255]) as readonly [number, number, number, number]
    return Object.freeze({
      id: player.identity,
      section: 0,
      cells: Object.freeze(cells),
      enabled: true,
      foregroundColor,
      backgroundColor: Object.freeze([0, 0, 0, 80]) as readonly [number, number, number, number],
    })
  }

  #publishScoreboard(value: Tf2HudScoreboard): void {
    if (this.#publishedScoreboard === value) return
    this.#publishedScoreboard = value
    const root = this.#panels.get("scoreinfo")
    if (root === undefined) throw new Error("TF2 scoreboard authored root is absent")
    const widths = {
      medal: this.#scoreboardColumnWidth("medal_column_width"),
      avatar: this.#scoreboardColumnWidth("avatar_width"),
      spacer: this.#scoreboardColumnWidth("spacer"),
      killstreak: this.#scoreboardColumnWidth("killstreak_width"),
      killstreakImage: this.#scoreboardColumnWidth("killstreak_image_width"),
      nemesis: this.#scoreboardColumnWidth("nemesis_width"),
      score: this.#scoreboardColumnWidth("score_width"),
      class: this.#scoreboardColumnWidth("class_width"),
      ping: this.#scoreboardColumnWidth("ping_width"),
    }
    for (const [name, team] of [["RedPlayerList", 2], ["BluePlayerList", 3]] as const) {
      const id = this.#panels.get(name.toLowerCase())
      if (id === undefined) throw new Error(`TF2 scoreboard authored list is absent: ${name}`)
      const panel = this.#runtime.snapshotPanels([id])[0]!
      const reserved = widths.medal + widths.avatar + widths.spacer + widths.killstreak
        + widths.killstreakImage + widths.nemesis * 2 + widths.score + widths.class + widths.ping + 10
      const nameWidth = Math.max(0, panel.bounds.width - reserved)
      const image = 0x02, center = 0x08, right = 0x10
      const columns = [
        { name: "medal", text: "", flags: image | center, width: widths.medal },
        { name: "avatar", text: "", flags: image, width: widths.avatar },
        { name: "spacer", text: "", flags: 0, width: widths.spacer },
        { name: "name", text: "#TF_Scoreboard_Name", flags: 0, width: nameWidth },
        { name: "killstreak", text: "", flags: right, width: widths.killstreak },
        { name: "killstreak_image", text: "", flags: image, width: widths.killstreakImage },
        { name: "dominating", text: "", flags: image | center, width: widths.nemesis },
        { name: "nemesis", text: "", flags: image | center, width: widths.nemesis },
        { name: "score", text: "#TF_Scoreboard_Score", flags: right, width: widths.score },
        { name: "class", text: "", flags: image | right, width: widths.class },
        { name: "ping", text: value.pingAsText ? "#TF_Scoreboard_Ping" : "", flags: value.pingAsText ? right : image | right, width: widths.ping },
      ]
      const sectionedItems = value.players.filter((player) => player.team === team)
        .map((player) => this.#scoreboardPlayer(player, value.pingAsText))
      const selected = sectionedItems.findIndex((player) => value.selectedPlayer.kind === "available" && player.id === value.selectedPlayer.value)
      apply(this.#runtime, { kind: "mutate-control", panel: id, mutation: {
        sections: [Object.freeze({ id: 0, name: "", alwaysVisible: true, minimumHeight: 0, columns: Object.freeze(columns) })],
        sectionedItems,
        activeIndex: selected >= 0 ? selected : null,
      } })
    }
    const spectators = value.spectators.length === 0 ? "" : (
      this.#localization.get((value.spectators.length === 1 ? "#ScoreBoard_Spectator" : "#ScoreBoard_Spectators").toLowerCase()) ?? ""
    ).replace("%s1", String(value.spectators.length)).replace("%s2", value.spectators.join(", "))
    apply(this.#runtime, { kind: "set-dialog-variable", panel: root, name: "spectators", value: spectators })
    apply(this.#runtime, { kind: "set-dialog-variable", panel: root, name: "waitingtoplay", value: value.waitingToPlay.join(", ") })
    apply(this.#runtime, { kind: "set-dialog-variable", panel: root, name: "mapname", value: value.mapName })
    apply(this.#runtime, { kind: "set-dialog-variable", panel: root, name: "server", value: "" })
    apply(this.#runtime, { kind: "set-dialog-variable", panel: root, name: "servertime", value: "" })
    const local = value.players.find((player) => player.identity === 1)
    apply(this.#runtime, { kind: "set-dialog-variable", panel: root, name: "playername", value: local?.name ?? "" })
    const stats = this.#panels.get("localplayerstatspanel")
    if (stats !== undefined) {
      const counters = local?.counters.kind === "available" ? local.counters.value : null
      for (const name of ["kills", "deaths", "assists", "destruction", "captures", "defenses", "dominations", "revenge", "healing", "invulns", "teleports", "headshots", "backstabs", "bonus", "support", "damage"] as const) {
        apply(this.#runtime, { kind: "set-dialog-variable", panel: stats, name, value: counters?.[name] ?? "" })
      }
      apply(this.#runtime, { kind: "set-dialog-variable", panel: stats, name: "gametype", value: value.gameType.kind === "available" ? this.#localization.get(value.gameType.value.toLowerCase()) ?? value.gameType.value : "" })
    }
  }

  #applyValues(binding: Tf2HudBinding): void {
    for (const value of binding.values) this.#value(value)
    if (binding.scoreboard.kind === "available") this.#publishScoreboard(binding.scoreboard.value)
    this.#crosshair.publish(binding, this.#viewport)
    this.#publishModelPanel(binding)
  }

  #publishModelPanel(binding: Tf2HudBinding): void {
    const player = binding.facts.player.kind === "available" ? binding.facts.player.value : null
    if (!player?.playerClassUsePlayerModel || player.lifecycle !== "active" || player.liveHudSuppressed
      || player.class.kind !== "available" || player.classModel.kind !== "available") {
      this.#modelPanel = null
      this.#modelPanelFingerprint = ""
      return
    }
    const playerClass = player.spy?.disguise?.class ?? player.class.value
    const fingerprint = `${player.classModel.value.identity}:${player.classModel.value.skin}:${playerClass}:${this.#viewport.width}x${this.#viewport.height}`
    if (fingerprint === this.#modelPanelFingerprint && this.#modelPanel) return
    const identity = this.#panels.get("classmodelpanel")
    const panel = identity === undefined ? null : this.#runtime.snapshotPanels([identity])[0]
    if (!panel?.visible) {
      this.#modelPanel = null
      this.#modelPanelFingerprint = ""
      return
    }
    const authored = this.#resources.document(HUD_CLASS).root.children.find((value) => value.name === "classmodelpanel")
    const classes = authored?.children.find((value) => value.name.toLowerCase() === "customclassdata" && value.value === null)
    const className = tf2ClassPresentation(playerClass).displayName
    const selected = classes?.children.find((value) => value.name.toLowerCase() === className.toLowerCase())
    if (!selected) throw new Error(`TF2 authored HUD class-model presentation is unavailable: ${className}`)
    const number = (name: string): number => {
      const value = scalar(selected, name)
      if (value === null || !Number.isFinite(Number(value))) {
        throw new Error(`TF2 authored HUD class-model ${className} field is invalid: ${name}`)
      }
      return Number(value)
    }
    this.#modelPanel = Object.freeze({
      model: player.classModel.value.identity,
      skin: player.classModel.value.skin,
      fov: number("fov"),
      origin: Object.freeze([number("origin_x"), number("origin_y"), number("origin_z")]) as readonly [number, number, number],
      angles: Object.freeze([number("angles_x"), number("angles_y"), number("angles_z")]) as readonly [number, number, number],
      bounds: Object.freeze({ ...panel.bounds }),
    })
    this.#modelPanelFingerprint = fingerprint
  }

  materialFrame(): import("@playsrc/rendering").HudMaterialFrame | undefined { return this.#scope.materialFrame() }

  publish(publication: SessionSimulationPublication, context: SessionHudContext): Tf2HudBinding {
    if (this.#destroyed) throw new Error("TF2 HUD integration is destroyed")
    const source = publication.snapshot
    if (!Number.isInteger(source.pipebombCount) || source.pipebombCount < 0 || source.pipebombCount > 31
      || source.chargeProgress !== null && (!Number.isFinite(source.chargeProgress) || source.chargeProgress < 0 || source.chargeProgress > 1)) {
      throw new Error("Demoman HUD count/charge is not authoritative")
    }
    return this.#runtime.deferPresentation(() => {
    const adapted = adaptSessionHud(this.#previous, publication, context)
    const binding = bindTf2Hud(adapted)
    this.#applyValues(binding)
    const scoped = publication.snapshot.class === 2 && publication.snapshot.weapon === 12
      && (publication.snapshot.conditions[0] & 2) !== 0
    if (scoped) {
      const state = publication.snapshot.loadout.find(value => value.weapon === 12) as { chargedDamage?: number } | undefined
      if (!state || state.chargedDamage === undefined) throw new Error("TF2 Sniper scope has no authoritative charge")
      this.#scope.publish(true, state.chargedDamage, this.#viewport)
    } else {
      this.#scope.hide()
    }
    let round = publication.snapshot.round
    if (round) {
      let retained: Array<RoundSnapshot["events"][number]> | undefined
      for (const batch of publication.eventBatches) {
        if (batch.snapshot.tick <= this.#lastRoundEventTick) continue
        if (batch.snapshot.round?.events.length) (retained ??= []).push(...batch.snapshot.round.events)
        this.#lastRoundEventTick = batch.snapshot.tick
      }
      const events = retained ?? NO_ROUND_EVENTS
      if (events.length !== 0 || round.events.length !== 0) round = { ...round, events }
      this.#publishRound(round, publication.snapshot.team)
    }
    const objectives = publication.snapshot.objectives
    if (publication.snapshot.controlPoints && !this.#controlPoints) {
      this.#controlPoints = new ControlPointHud(this.#runtime, this.#resources, (panel, resource) => applyPanelResource(this.#runtime, panel, this.#resources.document(resource), { activeConditions: this.#resources.activeConditions, resolutionSuffixes: this.#resources.resolutionSuffixes }))
    }
    this.#controlPoints?.publish(publication.snapshot.controlPoints, round, publication.snapshot.team, this.#viewport, Number(publication.snapshot.tick) * 0.015, context.liveHudSuppressed)
    if (objectives) {
      this.#publishObjectives(objectives, context.playerIdentity, publication.snapshot.team, publication.snapshot.tick)
    } else if (this.#objective) {
      apply(this.#runtime, { kind: "set-panel-state", panel: this.#objective.root, visible: false })
      apply(this.#runtime, { kind: "set-panel-state", panel: this.#objective.notification, visible: false })
    }
    this.#publishWinPanel(round, objectives ?? undefined, publication, binding.scoreboard.kind === "available" ? binding.scoreboard.value : undefined)
    this.#deathNotices.publish(binding, Math.fround(Math.fround(Number(publication.snapshot.tick)) * Math.fround(0.015)), Math.fround(0.015))
    const countMeter = weaponCountMeter(publication.snapshot, context.inventory)
    const countLive = binding.facts.player.kind === "available" && !binding.facts.player.value.liveHudSuppressed
    const live = countLive && source.lifecycle === 1 && (source.conditions[2] & (1 << (77 - 64))) === 0
    this.#demoman = { pipesVisible: live && source.class === 4, chargeVisible: live && source.chargeProgress !== null,
      count: source.pipebombCount, progress: source.chargeProgress }
    this.#objectiveValue("pipes:visible", String(this.#demoman.pipesVisible), { kind: "set-panel-state", panel: this.#demomanPanels.pipes, visible: this.#demoman.pipesVisible })
    this.#objectiveValue("charge:visible", String(this.#demoman.chargeVisible), { kind: "set-panel-state", panel: this.#demomanPanels.charge, visible: this.#demoman.chargeVisible })
    if (source.team === 2 || source.team === 3) for (const image of this.#demomanPanels.images) {
      const value = source.team === 2 ? image.red : image.blue
      this.#objectiveValue(`demoman-image:${image.panel}`, value, { kind: "mutate-control", panel: image.panel, mutation: { image: value } })
    }
    for (const [kind, meter] of this.#countMeters) {
      const visible = countLive && countMeter?.kind === kind
      this.#objectiveValue(`count:${kind}:visible`, String(visible), { kind: "set-panel-state", panel: meter.root, visible })
      if (!visible || !countMeter) continue
      this.#objectiveValue(`count:${kind}:value`, String(countMeter.count), { kind: "set-dialog-variable", panel: meter.root, name: "progresscount", value: countMeter.count })
      const image = publication.snapshot.team === 2 ? "../hud/misc_ammo_area_red" : "../hud/misc_ammo_area_blue"
      this.#objectiveValue(`count:${kind}:background`, image, { kind: "mutate-control", panel: meter.background, mutation: { image } })
    }
    const medic = this.#medicPanels
    if (medic) {
      const source = publication.snapshot as typeof publication.snapshot & { medigunCharge: number; medigunReleasing: boolean }
      const visible = source.class === 5 && source.lifecycle === 1 && (source.weapon === 20 || source.weapon === 21)
      this.#objectiveValue("medic-visible", String(visible), { kind: "set-panel-state", panel: medic.root, visible })
      if (visible) {
        const charge = source.medigunCharge
        if (!Number.isFinite(charge) || charge < 0 || charge > 1) throw new Error("Medi Gun HUD charge is not authoritative")
        const percent = Math.floor(charge * 100)
        this.#objectiveValue("medic-charge-percent", String(percent), { kind: "set-dialog-variable", panel: medic.root, name: "charge", value: percent })
        if (medic.meter !== null) {
          this.#objectiveValue("medic-charge-progress", String(charge), { kind: "mutate-control", panel: medic.meter, mutation: { scalarProperties: { progress: charge } } })
        }
        if (medic.background !== null) {
          const image = source.team === 2 ? "../hud/medic_charge_red_bg" : "../hud/medic_charge_blue_bg"
          this.#objectiveValue("medic-charge-background", image, { kind: "mutate-control", panel: medic.background, mutation: { image } })
        }
        for (const panel of medic.hidden) {
          this.#objectiveValue(`medic-hidden:${panel}`, "false", { kind: "set-panel-state", panel, visible: false })
        }
      }
    }
    for (const animation of binding.animations) {
      const parent = animation.target === "viewport" ? 1 : this.#panels.get(animation.target.toLowerCase()) ?? null
      if (parent === null) {
        this.#diagnostic("AnimationUnavailable", `${animation.target}:${animation.sequence}`)
        continue
      }
      try {
        apply(this.#runtime, { kind: "start-animation-sequence", parent, sequence: animation.sequence, cancelable: true })
        this.#animationTrace.push(`${animation.tick}:${animation.ordinal}:${animation.target}:${animation.sequence}`)
      } catch {
        this.#diagnostic("AnimationUnavailable", `${animation.target}:${animation.sequence}`)
      }
    }
    this.#previous = tf2HudAvailable(binding.facts)
    this.#binding = binding
    this.#damage?.publish(binding, this.#clock.nowSeconds())
    return binding
    })
  }

  action(action: Tf2HudAction): Tf2HudAvailability<Tf2HudCommand> {
    if (!this.#binding) return tf2HudUnavailable("initial")
    const command = bindTf2HudAction(this.#binding.facts, action)
    if (command.kind === "available") this.#onCommand(command.value)
    return command
  }

  frame(timeSeconds: number, visible = true): void {
    const milliseconds = Math.floor(timeSeconds * 1000)
    if (!visible) {
      if (milliseconds >= this.#pipeTick) this.#pipeTick = milliseconds + 100
      return
    }
    this.#runtime.deferPresentation(() => {
      apply(this.#runtime, { kind: "frame", timeSeconds })
      const state = this.#demoman, panels = this.#demomanPanels
      if (milliseconds >= this.#pipeTick) {
        this.#pipeTick = milliseconds + 100
        if (state?.pipesVisible) {
          if (state.count !== this.#lastPipeCount) {
            for (const panel of [panels.present, panels.absent]) apply(this.#runtime, { kind: "set-dialog-variable", panel, name: "activepipes", value: state.count })
            apply(this.#runtime, { kind: "set-panel-state", panel: panels.present, visible: state.count > 0 })
            apply(this.#runtime, { kind: "set-panel-state", panel: panels.absent, visible: state.count <= 0 })
            this.#lastPipeCount = state.count
          }
          for (const panel of [panels.shieldLabel, panels.shieldMeter]) this.#objectiveValue(`shield-hidden:${panel}`, "false", { kind: "set-panel-state", panel, visible: false })
        }
      }
      if (state && state.progress !== null) this.#objectiveValue("launcher-charge", String(state.progress), {
        kind: "mutate-control", panel: panels.chargeMeter, mutation: { progress: state.progress },
      })
    })
    this.#damage?.frame(timeSeconds, this.#viewport)
    for (const deltas of this.#timerDeltas.values()) if (deltas.active) {
      this.#runtime.deferPresentation(() => {
        for (const active of this.#timerDeltas.values()) active.frame(timeSeconds)
      })
      break
    }
  }
  setViewport(viewport: VguiViewport): void {
    if (viewport.width === this.#viewport.width
      && viewport.height === this.#viewport.height
      && viewport.devicePixelRatio === this.#viewport.devicePixelRatio) return
    this.#runtime.deferPresentation(() => {
      apply(this.#runtime, { kind: "set-viewport", viewport })
      this.#viewport = Object.freeze({ ...viewport })
      this.#scope.setViewport(viewport)
      this.#deathNotices.setViewport(viewport)
      this.#captureBaseBounds()
      if (this.#timerDeltas.size) {
        const panels = this.#runtime.snapshot().panels
        for (const [root, deltas] of this.#timerDeltas) deltas.resize(panels.find(panel => panel.id === root)!)
      }
      if (this.#binding) {
        this.#publishedValues.clear()
        this.#publishedScoreboard = undefined
        this.#applyValues(this.#binding)
      }
    })
  }
  probe(): Tf2HudIntegrationProbe {
    const panels = [
      "PlayerStatusHealthImage", "HudWeaponAmmo", "HudWeaponAmmoBG", "modelpanel0",
      "HudDemomanPipes", "PipesPresentPanel", "NoPipesPresentPanel", "HudDemomanCharge",
      "PlayerStatusClassImage", "PlayerStatusClassImageBG", "classmodelpanel", "classmodelpanelBG",
      "PlayerStatusSpyImage", "PlayerStatusSpyOutlineImage", "PlayerStatus_WheelOfDoom", "scoreinfo",
      "HudObjectiveStatus", "ObjectiveStatusFlagPanel", "BlueFlag", "RedFlag", "CarriedImage", "CaptureFlag", "BlueScore", "RedScore", "PlayingTo", "NotificationPanel", "Notification_Label", "WinPanel", "WinningTeamLabel", "WinReasonLabel",
      "scoreinfo", "RedPlayerList", "BluePlayerList", "Spectators", "Kills", "Deaths",
      ...TF2_GROUPED_CONDITION_PANELS.map((item) => item.panel),
      ...TF2_INDEPENDENT_CONDITION_PANELS.map((item) => item.panel),
    ]
      .map((name) => this.#panels.get(name.toLowerCase()))
      .filter((panel): panel is VguiPanelId => panel !== undefined)
    return Object.freeze({
      panels: this.#runtime.snapshotPanels(panels),
      animationTrace: Object.freeze([...this.#animationTrace]),
    })
  }
  modelPanel(): Tf2HudModelPanel | null { return this.#modelPanel }
  snapshot(): Tf2HudIntegrationSnapshot {
    return Object.freeze({
      binding: this.#binding,
      vgui: this.#runtime.snapshot(),
      diagnostics: Object.freeze([...this.#diagnostics]),
      animationTrace: Object.freeze([...this.#animationTrace]),
    })
  }
  setPlayerClassUsePlayerModel(value: boolean): void {
    if (this.#destroyed) throw new Error("TF2 HUD integration is destroyed")
    if (typeof value !== "boolean") throw new Error("TF2 HUD player-model setting is invalid")
    const current = this.#binding
    if (!current || current.facts.player.kind !== "available") return
    const currentPlayer = current.facts.player.value
    this.#runtime.deferPresentation(() => {
      const player = Object.freeze({ ...currentPlayer, playerClassUsePlayerModel: value })
      const snapshot: Tf2HudSnapshot = Object.freeze({ ...current.facts, player: tf2HudAvailable(player) })
      const binding = bindTf2Hud(Object.freeze({
        previous: tf2HudUnavailable<Tf2HudSnapshot>("replay-discontinuity"),
        snapshot,
        events: Object.freeze([]),
      }))
      this.#publishedValues.clear()
      this.#applyValues(binding)
      this.#previous = tf2HudAvailable(binding.facts)
      this.#binding = binding
    })
  }
  setCrosshair(value: Tf2HudCrosshair): void {
    if (this.#destroyed) throw new Error("TF2 HUD integration is destroyed")
    const current = this.#binding
    if (!current || current.facts.player.kind !== "available") return
    const previousPlayer = current.facts.player.value
    this.#runtime.deferPresentation(() => {
      const player = Object.freeze({ ...previousPlayer, crosshair: tf2HudAvailable(value) })
      const snapshot: Tf2HudSnapshot = Object.freeze({ ...current.facts, player: tf2HudAvailable(player) })
      const binding = bindTf2Hud(Object.freeze({
        previous: tf2HudUnavailable<Tf2HudSnapshot>("replay-discontinuity"),
        snapshot,
        events: Object.freeze([]),
      }))
      this.#applyValues(binding)
      this.#previous = tf2HudAvailable(binding.facts)
      this.#binding = binding
    })
  }

  setScoreboardVisibility(visible: boolean): void {
    if (this.#destroyed) throw new Error("TF2 HUD integration is destroyed")
    const current = this.#binding
    if (!current || current.scoreboard.kind !== "available" || current.scoreboard.value.visible === visible) return
    this.#runtime.deferPresentation(() => {
      const scoreboard = tf2HudAvailable(Object.freeze({ ...current.scoreboard.value, visible }))
      const snapshot: Tf2HudSnapshot = Object.freeze({ ...current.facts, scoreboard })
      const binding = bindTf2Hud(Object.freeze({
        previous: tf2HudUnavailable<Tf2HudSnapshot>("replay-discontinuity"),
        snapshot,
        events: Object.freeze([]),
      }))
      this.#applyValues(binding)
      this.#previous = tf2HudAvailable(binding.facts)
      this.#binding = binding
    })
  }

  setDeathNoticeTime(seconds: number): void { this.#deathNotices.setDuration(seconds) }

  reset(reason: "map-replaced" | "disconnect"): void {
    if (this.#destroyed) throw new Error("TF2 HUD integration is destroyed")
    void reason
    this.#runtime.deferPresentation(() => {
      const unavailable = tf2HudUnavailable<never>("replay-discontinuity")
      const snapshot: Tf2HudSnapshot = Object.freeze({
        tick: 0n,
        player: unavailable,
        scoreboard: unavailable,
        freezePanel: unavailable,
      })
      const binding = bindTf2Hud(Object.freeze({ previous: unavailable, snapshot, events: Object.freeze([]) }))
      this.#publishedValues.clear()
      this.#publishedScoreboard = undefined
      this.#applyValues(binding)
      this.#previous = unavailable
      this.#binding = null
      this.#scope.hide()
      this.#demoman = null
      for (const panel of [this.#demomanPanels.pipes, this.#demomanPanels.charge]) apply(this.#runtime, { kind: "set-panel-state", panel, visible: false })
      this.#damage?.reset()
      this.#animationTrace.length = 0
      if (this.#objective) {
        apply(this.#runtime, { kind: "set-panel-state", panel: this.#objective.root, visible: false })
        apply(this.#runtime, { kind: "set-panel-state", panel: this.#objective.notification, visible: false })
      }
      if (this.#winPanel !== undefined) {
        apply(this.#runtime, { kind: "set-panel-state", panel: this.#winPanel, visible: false })
      }
      if (this.#roundPanels) {
        apply(this.#runtime, { kind: "set-panel-state", panel: this.#roundPanels.match, visible: false })
        apply(this.#runtime, { kind: "set-panel-state", panel: this.#roundPanels.waiting, visible: false })
      }
      if (this.#kothPanels) {
        apply(this.#runtime, { kind: "set-panel-state", panel: this.#kothPanels.root, visible: false })
        for (const panel of this.#kothPanels.timers) apply(this.#runtime, { kind: "stop-animation-sequence", parent: panel.root, sequence: "OvertimeLabelPulseRed" })
      }
      this.#kothOvertime = [false, false]
      this.#kothActive = -1
      this.#lastRoundEventTick = -1n
      for (const deltas of this.#timerDeltas.values()) deltas.destroy()
      this.#timerDeltas.clear()
      this.#notificationDeadline = 0n
      this.#objectiveCarrying = false
      this.#waitingPanelVisible = false
      this.#waitingPanelEnding = false
      this.#deathNotices.reset()
    })
  }
  destroy(): void {
    if (this.#destroyed) return
    this.#destroyed = true
    this.#crosshair.destroy()
    this.#scope.destroy()
    this.#deathNotices.destroy()
    this.#damage?.reset()
    apply(this.#runtime, { kind: "destroy" })
  }
}

export function initializeTf2HudIntegration(request: Tf2HudIntegrationRequest): Tf2HudIntegration {
  return Object.freeze(new Integration(request))
}
